import { atom, onConnect } from '@reatom/framework'

import { Array, AsyncStateN, Order, pipe, Result } from '@blade/std-lib'
import { isAdminAtom } from '@root/entities/auth'
import { supportMessageCreatedEvent } from '@root/entities/realtime'
import { UserShort } from '@root/entities/user-short'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { environment } from '@root/shared'
import { reatomResourcePaginated } from '@root/shared/lib'

import { supportTicketStatusToggleAdminEvent } from '../atoms/reatom-support-ticket-admin'
import {
  fetchSupportTicketMessagesRequestAction,
  sendSupportTicketMessageRequestAction,
} from '../atoms/support-requests'
import { sendSupportAdminTicketMessageRequestAction } from '../atoms/support-requests-admin'

import { SupportTicketId } from './support-ticket-id'
import * as SupportTicketMessage from './support-ticket-message'

import type { tradeApi } from '@blade/socket-types'
import type {
  SupportAdminTicketsListParams,
  SupportTicketsListData,
} from '@blade/trade-api-client'
import type { AtomMut, Ctx } from '@reatom/framework'
import type { PaginatedData } from '@root/shared/lib'

export type SupportTicketAdminType = NonNullable<
  SupportAdminTicketsListParams['status']
>

type TicketDto = SupportTicketsListData['data'][number]
export type Status = TicketDto['status']

export class SupportTicket {
  public id: SupportTicketId
  public subject: string
  public lastMessagePreview: string
  public createdAt: Date
  public updatedAt: AtomMut<Date>
  public lastMessageAt: AtomMut<Date>
  public status: AtomMut<Status>
  public unreadCount: AtomMut<number>
  public author: UserShort.UserShort
  public messages

  constructor(dto: TicketDto) {
    this.id = SupportTicketId(dto.id)
    this.subject = dto.subject
    this.lastMessagePreview = dto.lastMessagePreview
    this.createdAt = new Date(dto.createdAt)
    this.updatedAt = atom(new Date(dto.updatedAt))
    this.lastMessageAt = atom(new Date(dto.lastMessageAt))
    this.status = atom(dto.status)
    this.unreadCount = atom(dto.unreadCount)
    this.author = UserShort.fromDto(
      {
        delugeId: dto.delugeId,
        username: dto.user?.username ?? dto.delugeId,
        firstName: dto.user?.firstName,
        lastName: dto.user?.lastName,
        avatar: dto.user?.avatar,
        isOnline: dto.user?.isOnline,
      },
      environment.assets.avatarsHost,
    )

    this.messages = reatomResourcePaginated(
      async (ctx, { limit, offset }) => {
        const result = await ctx.schedule(() =>
          fetchSupportTicketMessagesRequestAction(ctx, {
            ticketId: this.id,
            limit,
            offset,
          }),
        )
        return result.pipe(
          Result.map(
            (it): PaginatedData<SupportTicketMessage.Message, 'offset'> => ({
              data: it.data.map((dto) => SupportTicketMessage.fromDto(dto)),
            }),
          ),
        )
      },
      {
        limit: 20,
        strategy: 'offset',
        order: SupportTicketMessage.cmpByCreatedAt,
      },
    )

    onConnect(this.messages, (ctx) => {
      this.startListenMessagesUpdates(ctx)

      const unsubMessages = supportMessageCreatedEvent.onCall((ctx, data) => {
        if (data.ticketId !== this.id) {
          return
        }
        this.handleMessageUpdate(ctx, data)
      })

      return () => {
        unsubMessages()
        this.stopListenMessagesUpdates(ctx)
      }
    })
  }

  public updateFromDto = (ctx: Ctx, dto: TicketDto) => {
    this.updatedAt(ctx, new Date(dto.updatedAt))
    this.lastMessageAt(ctx, new Date(dto.lastMessageAt))
    this.status(ctx, dto.status)
    this.unreadCount(ctx, dto.unreadCount)
  }

  public startListenMessagesUpdates = (ctx: Ctx) => {
    const { mainWebsocket } = ctx.get(dependenciesAtom)

    mainWebsocket.supportTicketChatSubscribeAction(ctx, {
      ticketId: this.id,
    })
  }

  public stopListenMessagesUpdates = (ctx: Ctx) => {
    const { mainWebsocket } = ctx.get(dependenciesAtom)

    mainWebsocket.supportTicketChatUnsubscribeAction(ctx, {
      ticketId: this.id,
    })
  }

  private handleMessageUpdate = (
    ctx: Ctx,
    messageDto: tradeApi.SupportMessageData,
  ) => {
    const state = ctx.get(this.messages)
    if (!AsyncStateN.isSuccess(state)) {
      return
    }

    const message = SupportTicketMessage.fromDto(messageDto)

    const updatedMessages = pipe(
      state.value.data,
      Array.append(message),
      Array.sort(SupportTicketMessage.cmpByCreatedAt),
    )
    this.messages.setSuccess(ctx, {
      ...state.value,
      data: updatedMessages,
    })
  }

  public toggleStatus = async (ctx: Ctx) =>
    supportTicketStatusToggleAdminEvent(ctx, {
      ticket: this,
    })

  public sendMessage = async (ctx: Ctx, message: string) => {
    const isAdmin = ctx.get(isAdminAtom)

    const sendMessageAction = isAdmin
      ? sendSupportAdminTicketMessageRequestAction
      : sendSupportTicketMessageRequestAction

    const result = await ctx.schedule(() =>
      sendMessageAction(ctx, {
        ticketId: this.id,
        message,
      }),
    )

    return result
  }

  static cmpByUpdatedAt = (ctx: Ctx): Order.Order<SupportTicket> => {
    return (a, b) => {
      const updatedAtA = ctx.spy ? ctx.spy(a.updatedAt) : ctx.get(a.updatedAt)
      const updatedAtB = ctx.spy ? ctx.spy(b.updatedAt) : ctx.get(b.updatedAt)
      return Order.Date(updatedAtA, updatedAtB)
    }
  }
}
